import React, { useEffect, useMemo, useState } from 'react';
import garageAPI from '../../../services/garage/api';
import { formatApiError } from '../../../utils/formatApiError';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import './PersonaWebhookSettings.css';

const statusOf = (mapping) => {
  if (!mapping.configured) return { label: 'Not configured', tone: 'muted' };
  return mapping.enabled ? { label: 'Active', tone: 'on' } : { label: 'Paused', tone: 'off' };
};

const WebhookRow = ({ mapping, expanded, onToggle, onSaved, onRemoved, showMessage }) => {
  const [groupName, setGroupName] = useState(mapping.groupName);
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(mapping.configured ? mapping.enabled : true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => {
    setGroupName(mapping.groupName);
    setEnabled(mapping.configured ? mapping.enabled : true);
    setUrl('');
  }, [mapping]);

  const dirty = Boolean(url.trim()) || groupName !== mapping.groupName || enabled !== mapping.enabled;
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const body = { groupName, enabled };
      if (url.trim()) body.webhookUrl = url.trim();
      const saved = await garageAPI.savePersonaWebhook(mapping.personaId, body);
      setUrl('');
      onSaved(saved);
      showMessage('Saved', `Webhook configuration for ${mapping.personaLabel} was saved.`);
    } catch (error) {
      showMessage('Unable to save', formatApiError(error), 'error');
    } finally { setBusy(false); }
  };
  const confirmAction = async () => {
    setBusy(true);
    try {
      if (confirm === 'test') {
        const result = await garageAPI.testPersonaWebhook(mapping.personaId);
        showMessage('Test sent', result.message);
      } else {
        await garageAPI.removePersonaWebhook(mapping.personaId);
        setUrl('');
        onRemoved(mapping.personaId);
        showMessage('Removed', `Webhook configuration for ${mapping.personaLabel} was removed.`);
      }
    } catch (error) {
      showMessage('Action failed', formatApiError(error), 'error');
    } finally { setBusy(false); setConfirm(null); }
  };
  const fieldId = `persona-webhook-${mapping.personaId}`;
  const status = statusOf(mapping);
  const summary = mapping.configured
    ? (mapping.groupName || 'Destination saved')
    : 'No destination yet';
  return (
    <div className={`persona-webhook-card${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="persona-webhook-summary"
        aria-expanded={expanded}
        aria-controls={`${fieldId}-panel`}
        onClick={onToggle}
      >
        <span className="persona-webhook-summary-main">
          <span className="persona-webhook-name">{mapping.personaLabel}</span>
          <span className="persona-webhook-sub">{summary}</span>
        </span>
        <span className={`persona-webhook-pill persona-webhook-pill-${status.tone}`}>{status.label}</span>
        <svg className="persona-webhook-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      <form
        id={`${fieldId}-panel`}
        className="persona-webhook-panel"
        onSubmit={save}
        aria-label={`${mapping.personaLabel} webhook`}
        hidden={!expanded}
      >
        <fieldset disabled={busy}>
          <div className="config-form-row">
            <div className="config-form-group">
              <label htmlFor={`${fieldId}-group`}>Group name (optional)</label>
              <input id={`${fieldId}-group`} type="text" value={groupName} maxLength={120} onChange={(e) => setGroupName(e.target.value)} placeholder="e.g. IT Requests" />
            </div>
            <div className="config-form-group">
              <label htmlFor={`${fieldId}-url`}>{mapping.configured ? 'Replace webhook URL' : 'Webhook URL'}</label>
              <input id={`${fieldId}-url`} type="password" autoComplete="new-password" value={url} onChange={(e) => setUrl(e.target.value)} required={!mapping.configured} placeholder={mapping.configured ? 'Leave blank to keep the saved URL' : 'Paste the RingCentral incoming webhook URL'} />
              <small>{mapping.configured ? 'The saved URL is hidden. Enter a new URL only to replace it.' : 'The URL is encrypted when saved and is never displayed again.'}</small>
            </div>
          </div>
          <div className="persona-webhook-footer">
            <label className="persona-webhook-enabled"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enable automatic notifications</label>
            <div className="persona-webhook-actions">
              <button className="garage-btn garage-btn-secondary" type="button" disabled={!mapping.configured} onClick={() => setConfirm('remove')}>Remove</button>
              <button className="garage-btn garage-btn-secondary" type="button" disabled={!mapping.configured || dirty} onClick={() => setConfirm('test')}>Send test</button>
              <button className="garage-btn garage-btn-primary" type="submit" disabled={!dirty}>{busy ? 'Working…' : 'Save'}</button>
            </div>
          </div>
        </fieldset>
        {(mapping.configured && dirty) || mapping.updatedAt ? (
          <p className="persona-webhook-meta">
            {mapping.configured && dirty ? 'Save changes before sending a test to the saved destination.' : null}
            {mapping.configured && dirty && mapping.updatedAt ? ' · ' : null}
            {mapping.updatedAt ? `Updated ${new Date(mapping.updatedAt).toLocaleString()}${mapping.updatedByEmail ? ` by ${mapping.updatedByEmail}` : ''}` : null}
          </p>
        ) : null}
        <ConfirmModal isOpen={Boolean(confirm)} title={confirm === 'test' ? 'Send test card?' : 'Remove webhook?'} message={confirm === 'test' ? `Send one labeled test card to the saved ${mapping.personaLabel} destination? This works even when automatic notifications are disabled.` : `Stop webhook delivery for ${mapping.personaLabel} and remove its saved destination?`} confirmText={confirm === 'test' ? 'Send test' : 'Remove'} variant={confirm === 'remove' ? 'danger' : 'default'} isLoading={busy} onConfirm={confirmAction} onCancel={() => !busy && setConfirm(null)} />
      </form>
    </div>
  );
};

export default function PersonaWebhookSettings() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    let current = true;
    garageAPI.getPersonaWebhooks().then((rows) => {
      if (!current) return;
      setMappings(rows);
      if (rows.length === 1) setOpenId(rows[0].personaId);
    })
      .catch((err) => { if (current) setError(formatApiError(err)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, []);
  const showMessage = (title, message, variant = 'info') => setAlert({ title, message, variant });
  const configuredCount = mappings.filter((row) => row.configured).length;
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return mappings;
    return mappings.filter((row) => `${row.personaLabel} ${row.groupName || ''}`.toLowerCase().includes(needle));
  }, [mappings, query]);
  return (
    <section aria-label="Persona webhook configuration">
      <div className="config-toolbar">
        <div className="config-toolbar-info">
          <h3>Persona Webhooks</h3>
          <p className="config-toolbar-description">Choose the RingCentral group destination for each persona you manage. Select a persona to edit its destination.</p>
        </div>
        {mappings.length > 0 && (
          <span className="persona-webhook-count">{configuredCount} of {mappings.length} configured</span>
        )}
      </div>
      <p className="config-note">Automatic cards are sent for regular-user requests and comments. Admin actions and status changes do not trigger group webhooks.</p>
      {mappings.length > 6 && (
        <input className="persona-webhook-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter personas…" aria-label="Filter personas" />
      )}
      {loading ? <div className="config-loading">Loading destinations…</div>
        : error ? <p role="alert">{error}</p>
          : mappings.length === 0 ? <div className="config-empty">No active personas are available in your scope.</div>
            : visible.length === 0 ? <div className="config-empty">No personas match “{query}”.</div>
              : (
                <div className="persona-webhook-list">
                  {visible.map((mapping) => (
                    <WebhookRow
                      key={mapping.personaId}
                      mapping={mapping}
                      expanded={openId === mapping.personaId}
                      onToggle={() => setOpenId((current) => (current === mapping.personaId ? null : mapping.personaId))}
                      showMessage={showMessage}
                      onSaved={(saved) => setMappings((rows) => rows.map((row) => row.personaId === saved.personaId ? saved : row))}
                      onRemoved={(id) => setMappings((rows) => rows.map((row) => row.personaId === id ? { ...row, configured: false, enabled: false, groupName: '', updatedAt: null, updatedByEmail: null } : row))}
                    />
                  ))}
                </div>
              )}
      <AlertModal isOpen={Boolean(alert)} title={alert?.title} message={alert?.message} variant={alert?.variant} onClose={() => setAlert(null)} />
    </section>
  );
}
